import React, { useState, useRef } from 'react';
import './HelpCenter.css';
import { useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { createFeedback } from '../../api/feedback';

const feedbackCards = [
    {
        id: 'bug',
        icon: <BugReportIcon />,
        title: 'Report a Bug',
        desc: 'Found something broken? Let us know.',
    },
    {
        id: 'feature',
        icon: <LightbulbIcon />,
        title: 'Request a Feature',
        desc: 'Have an idea to improve the app?',
    },
    {
        id: 'general',
        icon: <ChatBubbleOutlineIcon />,
        title: 'General Feedback',
        desc: 'Share your thoughts with us.',
    },
    {
        id: 'issue',
        icon: <ReportProblemIcon />,
        title: 'Report an Issue',
        desc: 'Having a problem using the app?',
    },
];

const faqs = [
    {
        id: 'faq-1',
        question: 'How do I create a short video?',
        answer:
            'To create a short video, tap the "Upload Shorts" button from the sidebar or navigate to /upload-shorts. You can record or upload a video from your device (max 60 seconds). Add a title and description, then hit Publish. Your video will appear in the Shorts feed once processed.',
    },
    {
        id: 'faq-2',
        question: 'How do I share a ballot?',
        answer:
            'Open any ballot from the Ballots page and tap the share icon in the top-right corner of the ballot card. You can copy the link or share it directly to social media. Recipients will be directed to the ballot even if they aren\'t logged in — they\'ll just be prompted to sign in to vote.',
    },
    {
        id: 'faq-3',
        question: 'Can I edit my profile?',
        answer:
            'Yes! Head to Profile Details from the sidebar. You can update your name, date of birth, address, gender, and language preferences at any time. To update your cover photo, tap the camera icon on your profile header. Government ID verification can be managed separately from profile settings.',
    },
];

const HelpCenter = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(null);
    const [activeCard, setActiveCard] = useState(null);
    const [screenshot, setScreenshot] = useState(null);   // { file, preview }
    const [dragOver, setDragOver] = useState(false);
    const [feedbackText, setFeedbackText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const fileInputRef = useRef(null);

    const toggleFaq = (id) => {
        setOpenFaq((prev) => (prev === id ? null : id));
    };

    const handleFileSelect = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        const preview = URL.createObjectURL(file);
        setScreenshot({ file, preview });
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        handleFileSelect(file);
    };

    const removeScreenshot = () => {
        if (screenshot?.preview) URL.revokeObjectURL(screenshot.preview);
        setScreenshot(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSubmit = async () => {
        if (!feedbackText.trim()) return;
        const cardLabel = feedbackCards.find(c => c.id === activeCard)?.title || activeCard;
        setSubmitting(true);
        setSubmitStatus(null);
        try {
            await createFeedback({
                description: feedbackText.trim(),
                type: cardLabel,
                file: screenshot?.file || null,
            });
            setSubmitStatus('success');
            setFeedbackText('');
            removeScreenshot();
            setTimeout(() => {
                setActiveCard(null);
                setSubmitStatus(null);
            }, 2000);
        } catch (err) {
            console.error('Feedback submission error:', err);
            setSubmitStatus('error');
        } finally {
            setSubmitting(false);
        }
    };

    // Reset status when card changes
    const handleCardClick = (id) => {
        setActiveCard(id === activeCard ? null : id);
        setFeedbackText('');
        setSubmitStatus(null);
        removeScreenshot();
    };

    return (
        <div className="hc-page">
            <div className="hc-container">

                {/* ── Page Header ── */}
                <div className="hc-page-header">
                    <div className="hc-header-icon-wrap">
                        <HelpOutlineIcon className="hc-header-icon" />
                    </div>
                    <div>
                        <h1 className="hc-title">Help &amp; Support</h1>
                        <p className="hc-subtitle">
                            We're here to help! Get in touch with our team for any questions and feedback.
                        </p>
                    </div>
                </div>

                {/* ── Contact Us ── */}
                <section className="hc-section">
                    <h2 className="hc-section-title">Contact Us</h2>
                    <div className="hc-contact-card">
                        <div className="hc-contact-icon-wrap">
                            <EmailIcon className="hc-contact-icon" />
                        </div>
                        <div className="hc-contact-text">
                            <div className="hc-contact-label">Email Support</div>
                            <a href="mailto:support@amalgamate.ca" className="hc-contact-email">
                                support@amalgamate.ca
                            </a>
                            <p className="hc-contact-desc">
                                Send us your feedback, questions, or report an issue.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ── Send Feedback ── */}
                <section className="hc-section">
                    <h2 className="hc-section-title">Send Feedback</h2>
                    <p className="hc-section-subtitle">
                        Help us improve by reporting bugs, requesting features, or sharing your thoughts.
                    </p>
                    <div className="hc-feedback-grid">
                        {feedbackCards.map((card) => (
                            <button
                                key={card.id}
                                className={`hc-fb-card ${activeCard === card.id ? 'hc-fb-card--active' : ''}`}
                                onClick={() => handleCardClick(card.id)}
                            >
                                <div className="hc-fb-icon-wrap">
                                    {card.icon}
                                </div>
                                <div className="hc-fb-card-title">{card.title}</div>
                                <div className="hc-fb-card-desc">{card.desc}</div>
                            </button>
                        ))}
                    </div>
                    {activeCard && (
                        <div className="hc-feedback-form">
                            <textarea
                                className="hc-feedback-textarea"
                                placeholder={`Describe your ${feedbackCards.find(c => c.id === activeCard)?.title.toLowerCase()}…`}
                                rows={4}
                                value={feedbackText}
                                onChange={(e) => setFeedbackText(e.target.value)}
                                disabled={submitting}
                            />

                            {/* Screenshot upload */}
                            <div className="hc-upload-label">
                                Attach a Screenshot
                                <span className="hc-upload-optional">optional</span>
                            </div>

                            {screenshot ? (
                                <div className="hc-screenshot-preview">
                                    <img
                                        src={screenshot.preview}
                                        alt="Screenshot preview"
                                        className="hc-screenshot-img"
                                    />
                                    <button
                                        className="hc-screenshot-remove"
                                        onClick={removeScreenshot}
                                        title="Remove screenshot"
                                        disabled={submitting}
                                    >
                                        <CloseIcon style={{ fontSize: 16 }} />
                                    </button>
                                </div>
                            ) : (
                                <div
                                    className={`hc-drop-zone ${dragOver ? 'hc-drop-zone--over' : ''}`}
                                    onClick={() => !submitting && fileInputRef.current?.click()}
                                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                    onDragLeave={() => setDragOver(false)}
                                    onDrop={handleDrop}
                                >
                                    <AddPhotoAlternateIcon className="hc-drop-icon" />
                                    <span className="hc-drop-text">Click or drag &amp; drop an image here</span>
                                    <span className="hc-drop-hint">PNG, JPG, WEBP up to 10 MB</span>
                                </div>
                            )}

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => handleFileSelect(e.target.files[0])}
                            />

                            {/* Status messages */}
                            {submitStatus === 'success' && (
                                <div className="hc-submit-status hc-submit-status--success">
                                    <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                                    Feedback submitted successfully!
                                </div>
                            )}
                            {submitStatus === 'error' && (
                                <div className="hc-submit-status hc-submit-status--error">
                                    <ErrorOutlineIcon style={{ fontSize: 18 }} />
                                    Something went wrong. Please try again.
                                </div>
                            )}

                            <div className="hc-form-footer">
                                <button
                                    className="hc-feedback-cancel"
                                    onClick={() => handleCardClick(activeCard)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="hc-feedback-submit"
                                    onClick={handleSubmit}
                                    disabled={submitting || !feedbackText.trim()}
                                >
                                    {submitting ? 'Submitting…' : 'Submit'}
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* ── FAQ ── */}
                <section className="hc-section">
                    <h2 className="hc-section-title">Frequently Asked Questions</h2>
                    <div className="hc-faq-list">
                        {faqs.map((faq) => (
                            <div
                                key={faq.id}
                                className={`hc-faq-item ${openFaq === faq.id ? 'hc-faq-item--open' : ''}`}
                                onClick={() => toggleFaq(faq.id)}
                            >
                                <div className="hc-faq-question">
                                    <span>{faq.question}</span>
                                    {openFaq === faq.id
                                        ? <ExpandLessIcon className="hc-faq-chevron" />
                                        : <ExpandMoreIcon className="hc-faq-chevron" />
                                    }
                                </div>
                                {openFaq === faq.id && (
                                    <div className="hc-faq-answer">{faq.answer}</div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* ── Terms & Policies ── */}
                <section className="hc-section hc-policies-section">
                    <h2 className="hc-section-title">Terms &amp; Policies</h2>
                    <p className="hc-section-subtitle">
                        Review our legal agreements and privacy practices to understand how we handle your data and what governs your use of Amalgamate.
                    </p>
                    <div className="hc-policies-buttons">
                        <button
                            className="hc-policy-btn"
                            id="btn-terms-of-use"
                            onClick={() => navigate('/terms-of-use')}
                        >
                            <GavelIcon className="hc-policy-btn-icon" />
                            Terms of Use
                        </button>
                        <button
                            className="hc-policy-btn hc-policy-btn--secondary"
                            id="btn-privacy-policy"
                            onClick={() => navigate('/privacy-policy')}
                        >
                            <PrivacyTipIcon className="hc-policy-btn-icon" />
                            Privacy Policy
                        </button>
                    </div>
                </section>

            </div>
        </div>
    );
};

export default HelpCenter;
